import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plugs, Cpu, Bluetooth, FloppyDisk, ShareNetwork, Gauge, FileArrowUp, Warning } from '@phosphor-icons/react';
import { Card, CardContent } from '../components/ui/card';
import { CaretRight } from '@phosphor-icons/react';
import SerialService from '../services/serialService';
import BluetoothService from '../services/bluetoothService';

import ConnectionPanel from './controller/ConnectionPanel';
import TuningControls from './controller/TuningControls';
import FirmwareFlasher from './controller/FirmwareFlasher';
import ProfileList from './controller/ProfileList';
import TelemetryPanel from './controller/TelemetryPanel';

const CRITICAL_PARAMS = new Set([
    'phase_current_max', 'battery_current_max',
    'overcurrent_protect', 'undervoltage_cutoff',
    'overvoltage_cutoff', 'motor_temp_limit', 'controller_temp_limit'
]);

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const toHex = (bytes) => bytes.map(b => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');
const ts = () => new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });

export default function ControllerBay() {
    const { user } = useAuth();
    const [brands, setBrands] = useState([]);
    const [paramGroups, setParamGroups] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [sharedProfiles, setSharedProfiles] = useState([]);
    const [telemetry, setTelemetry] = useState(null);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedModel, setSelectedModel] = useState('');
    const [params, setParams] = useState({});
    const [profileName, setProfileName] = useState('');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [connectionMode, setConnectionMode] = useState(null);
    const [connectionId, setConnectionId] = useState(null);
    const [connectionInfo, setConnectionInfo] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [faultCodes, setFaultCodes] = useState(null);
    const [byteLog, setByteLog] = useState([]);
    const [readingParams, setReadingParams] = useState(false);
    const [writingParam, setWritingParam] = useState(null);
    const [telemetryInterval, setTelemetryIntervalId] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, paramId: null, value: null, label: '' });
    const [firmwareFile, setFirmwareFile] = useState(null);
    const [flashProgress, setFlashProgress] = useState(0);
    const [flashMessage, setFlashMessage] = useState('');
    const [isFlashing, setIsFlashing] = useState(false);
    const [rawHex, setRawHex] = useState('');

    const serialRef = useRef(null);
    const btRef = useRef(null);
    const protocolRef = useRef(null);

    const serialSupported = SerialService.isSupported();
    const btSupported = BluetoothService.isSupported();
    const isHardwareConnected = connectionMode === 'serial' || connectionMode === 'bluetooth';

    const addLog = useCallback((dir, bytes, label) => {
        setByteLog(prev => [...prev.slice(-200), { dir, hex: toHex(bytes), time: ts(), label }]);
    }, []);

    useEffect(() => {
        Promise.all([
            fetch(`${API}/controller/brands`).then(r => r.json()),
            fetch(`${API}/controller/params`).then(r => r.json()),
            fetch(`${API}/controller/profiles`, { credentials: 'include', headers: getH() }).then(r => r.json()),
            fetch(`${API}/controller/shared`).then(r => r.json()),
        ]).then(([b, p, pr, sh]) => {
            setBrands(b); setParamGroups(p); setProfiles(pr); setSharedProfiles(sh);
            const defaults = {};
            p.forEach(g => g.params.forEach(pm => { defaults[pm.id] = pm.default; }));
            setParams(defaults);
        }).catch(e => console.error('Controller init:', e)).finally(() => setLoading(false));
        return () => { serialRef.current?.disconnect(); btRef.current?.disconnect(); };
    }, []);

    const fetchProtocol = async (brandId) => {
        try { const res = await fetch(`${API}/controller/protocols/${brandId}`); if (res.ok) return await res.json(); } catch (e) { console.warn('Protocol:', e.message); }
        return null;
    };

    const registerConnection = async (method, port) => {
        try {
            const res = await fetch(`${API}/controller/connect`, { method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ brand: selectedBrand.id, model: selectedModel, method, port: port || 'web_serial' }) });
            if (res.ok) { const data = await res.json(); setConnectionId(data.connection_id); setConnectionInfo(data); fetch(`${API}/controller/fault-codes/${selectedBrand.id}`).then(r => r.json()).then(setFaultCodes).catch(e => console.warn('Fault:', e.message)); return data; }
        } catch (e) { console.warn('Register:', e.message); }
        return null;
    };

    const connectSerial = async () => {
        if (!selectedBrand) { toast.error('Select a controller brand first'); return; }
        setConnecting(true); setByteLog([]);
        try {
            const proto = await fetchProtocol(selectedBrand.id); if (!proto) throw new Error('Failed to load protocol');
            protocolRef.current = proto;
            const serial = new SerialService();
            serial.onData = (bytes) => addLog('rx', bytes);
            serial.onDisconnect = () => { setConnectionMode(null); setConnectionId(null); setConnectionInfo(null); toast.error('Serial port disconnected'); };
            await serial.connect({ baudRate: proto.baud_rate, dataBits: proto.data_bits, stopBits: proto.stop_bits, parity: proto.parity }, selectedBrand.id);
            serialRef.current = serial; setConnectionMode('serial');
            await registerConnection('uart', 'web_serial');
            toast.success(`Connected to ${selectedBrand.brand} ${selectedModel} via Web Serial`);
            addLog('tx', [], `Connected — ${proto.baud_rate} baud`);
        } catch (err) { toast.error(err.message || 'Serial connection failed'); } finally { setConnecting(false); }
    };

    const connectBluetooth = async (method) => {
        if (!selectedBrand) { toast.error('Select a controller brand first'); return; }
        setConnecting(true); setByteLog([]);
        try {
            const proto = await fetchProtocol(selectedBrand.id); if (!proto) throw new Error('Failed to load protocol');
            protocolRef.current = proto;
            const bt = new BluetoothService();
            bt.onData = (bytes) => addLog('rx', bytes);
            bt.onDisconnect = () => { setConnectionMode(null); setConnectionId(null); setConnectionInfo(null); toast.error('Bluetooth disconnected'); };
            const result = await bt.connect(selectedBrand.id);
            btRef.current = bt; setConnectionMode('bluetooth');
            await registerConnection(method, result.deviceName);
            toast.success(`Connected to ${result.deviceName || selectedBrand.brand} via ${method.toUpperCase()}`);
        } catch (err) { toast.error(err.message || 'Bluetooth connection failed'); } finally { setConnecting(false); }
    };

    const connectSimulated = async (method) => {
        if (!selectedBrand) { toast.error('Select a controller brand first'); return; }
        setConnecting(true);
        const connData = await registerConnection(method, 'simulated');
        if (connData) { setConnectionMode('simulated'); protocolRef.current = { baud_rate: connData.serial_config?.baudRate }; toast.success(`Simulated connection to ${selectedBrand.brand} ${selectedModel}`); }
        else toast.error('Connection failed');
        setConnecting(false);
    };

    const handleConnect = async (method) => {
        if (method === 'uart' && serialSupported) await connectSerial();
        else if ((method === 'bluetooth' || method === 'ble') && btSupported) await connectBluetooth(method);
        else await connectSimulated(method);
    };

    const handleDisconnect = async () => {
        if (connectionMode === 'serial') await serialRef.current?.disconnect();
        if (connectionMode === 'bluetooth') await btRef.current?.disconnect();
        if (connectionId) fetch(`${API}/controller/disconnect/${connectionId}`, { method: 'POST', credentials: 'include', headers: getH() }).catch(() => {});
        if (telemetryInterval) { clearInterval(telemetryInterval); setTelemetryIntervalId(null); }
        setConnectionMode(null); setConnectionId(null); setConnectionInfo(null);
        serialRef.current = null; btRef.current = null;
        toast.success('Disconnected');
    };

    const readAllParams = async () => {
        setReadingParams(true);
        try {
            if (connectionMode === 'serial' && serialRef.current) {
                const readCmd = connectionInfo?.read_command || [];
                addLog('tx', readCmd, 'READ_ALL');
                const result = await serialRef.current.readParams(readCmd, connectionInfo?.param_addresses || {});
                if (result.success && Object.keys(result.params).length > 0) { setParams(prev => ({ ...prev, ...result.params })); toast.success(`Read ${Object.keys(result.params).length} params`); }
                else toast.info(result.error || 'No param data parsed');
            } else if (connectionMode === 'bluetooth' && btRef.current) {
                const readCmd = connectionInfo?.read_command || [];
                addLog('tx', readCmd, 'BLE_READ_ALL');
                const result = await btRef.current.readParams(readCmd, connectionInfo?.param_addresses || {});
                if (result.success && Object.keys(result.params).length > 0) { setParams(prev => ({ ...prev, ...result.params })); toast.success(`Read ${Object.keys(result.params).length} params via BLE`); }
                else toast.info(result.error || 'No BLE response');
            } else {
                const res = await fetch(`${API}/controller/read-params`, { method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ connection_id: connectionId }) });
                if (res.ok) { const d = await res.json(); setParams(d.params); toast.success('Parameters read (simulated)'); }
            }
        } catch (err) { toast.error('Read failed: ' + (err.message || 'Unknown error')); } finally { setReadingParams(false); }
    };

    const requestWrite = (paramId, value, label) => {
        if (CRITICAL_PARAMS.has(paramId)) setConfirmDialog({ open: true, paramId, value, label: label || paramId });
        else writeParam(paramId, value);
    };

    const confirmWrite = () => { const { paramId, value } = confirmDialog; setConfirmDialog({ open: false, paramId: null, value: null, label: '' }); writeParam(paramId, value); };

    const writeParam = async (paramId, value) => {
        if (!connectionId) return;
        setWritingParam(paramId);
        try {
            if ((connectionMode === 'serial' && serialRef.current) || (connectionMode === 'bluetooth' && btRef.current)) {
                const writePrefix = connectionInfo?.write_prefix || [];
                const addr = connectionInfo?.param_addresses?.[paramId] || 0x00;
                const svc = connectionMode === 'serial' ? serialRef.current : btRef.current;
                addLog('tx', [...writePrefix, addr, (value >> 8) & 0xFF, value & 0xFF], `WRITE ${paramId}=${value}`);
                const result = await svc.writeParam(writePrefix, addr, Math.round(value));
                if (result.ackReceived) toast.success(`${paramId} written`); else toast.warning(`No ACK — verify ${paramId}`);
            } else {
                const res = await fetch(`${API}/controller/write-param`, { method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ brand: selectedBrand.id, param_id: paramId, value, connection_id: connectionId }) });
                if (res.ok) { const d = await res.json(); toast.success(`${paramId} = ${value} (simulated)`); addLog('tx', d.write_bytes || [], `SIM WRITE ${paramId}=${value}`); }
            }
        } catch (err) { toast.error(`Write failed: ${err.message}`); } finally { setWritingParam(null); }
    };

    const handleFirmwareSelect = (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        const validExts = ['.bin', '.hex', '.fw', '.img'];
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!validExts.includes(ext)) { toast.error('Invalid firmware file'); return; }
        setFirmwareFile(file); setFlashProgress(0); setFlashMessage(`Selected: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
    };

    const flashFirmware = async () => {
        if (!firmwareFile || !serialRef.current) return;
        setIsFlashing(true); setFlashProgress(0);
        try {
            const firmware = new Uint8Array(await firmwareFile.arrayBuffer());
            await serialRef.current.flashFirmware(firmware, (pct, msg) => { setFlashProgress(pct); setFlashMessage(msg); });
            toast.success('Firmware flashed successfully!');
            fetch(`${API}/controller/firmware-log`, { method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ connection_id: connectionId, brand: selectedBrand?.id, model: selectedModel, firmware_name: firmwareFile.name, firmware_size: firmwareFile.size, status: 'success' }) }).catch(() => {});
        } catch (err) { toast.error(`Flash failed: ${err.message}`); setFlashMessage(`Error: ${err.message}`); } finally { setIsFlashing(false); }
    };

    const startTelemetry = () => {
        if (!connectionId) return;
        if (telemetryInterval) { clearInterval(telemetryInterval); setTelemetryIntervalId(null); setTelemetry(null); return; }
        const poll = async () => {
            if ((connectionMode === 'serial' && serialRef.current) || (connectionMode === 'bluetooth' && btRef.current)) {
                const svc = connectionMode === 'serial' ? serialRef.current : btRef.current;
                const response = await svc.sendAndReceive(connectionInfo?.read_command || [], 1000);
                if (response.length >= 12) setTelemetry({ voltage: ((response[0] << 8) | response[1]) / 10, current: ((response[2] << 8) | response[3]) / 10, rpm: (response[4] << 8) | response[5], speed_kmh: (response[6] << 8) | response[7], temperature: { motor: response[8], controller: response[9] }, throttle: response[10], mode: response[11] });
            } else {
                try { const res = await fetch(`${API}/controller/telemetry/live`, { credentials: 'include', headers: getH() }); if (res.ok) setTelemetry(await res.json()); } catch (e) { console.warn("Telemetry:", e.message); }
            }
        };
        poll(); setTelemetryIntervalId(setInterval(poll, 1000));
    };

    const sendRawBytes = async () => {
        if (!rawHex.trim()) return;
        try {
            const bytes = rawHex.trim().split(/[\s,]+/).map(h => parseInt(h, 16)).filter(n => !isNaN(n));
            if (bytes.length === 0) { toast.error('Invalid hex'); return; }
            addLog('tx', bytes, 'RAW');
            const svc = connectionMode === 'serial' ? serialRef.current : btRef.current;
            if (svc) { const response = await svc.sendAndReceive(bytes, 2000); if (response.length > 0) toast.success(`Got ${response.length} bytes back`); else toast.info('No response (timeout)'); }
            setRawHex('');
        } catch (err) { toast.error(err.message); }
    };

    const saveProfile = async () => {
        if (!profileName.trim()) { toast.error('Enter a profile name'); return; }
        setSaving(true);
        try {
            const res = await fetch(`${API}/controller/profiles`, { method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: profileName, controller_brand: selectedBrand?.brand || '', controller_model: selectedModel, params }) });
            if (res.ok) { toast.success('Profile saved!'); setProfileName(''); const d = await res.json(); setProfiles(p => [d, ...p]); }
            else { const e = await res.json(); toast.error(e.detail || 'Save failed'); }
        } catch { toast.error('Save failed'); } finally { setSaving(false); }
    };

    const loadProfile = (p) => { setParams(p.params || {}); setProfileName(p.name); toast.success(`Loaded: ${p.name}`); };
    const deleteProfile = async (id) => { await fetch(`${API}/controller/profiles/${id}`, { method: 'DELETE', credentials: 'include', headers: getH() }); setProfiles(p => p.filter(x => x.profile_id !== id)); toast.success('Deleted'); };
    const shareProfile = async (id) => { const res = await fetch(`${API}/controller/profiles/${id}/share`, { method: 'POST', credentials: 'include', headers: getH() }); if (res.ok) toast.success('Shared publicly!'); };

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-cyber-purple text-glow-purple">PROGRAMMING BAY</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display" data-testid="controller-bay-title">Controller Tuning</h1>
                    <p className="text-muted-foreground text-sm mt-1">Configure EV controller parameters, save presets, and share with riders</p>
                    <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className={`text-[10px] ${serialSupported ? 'border-primary/30 text-primary' : 'border-white/10 text-muted-foreground'}`}>
                            <Plugs size={10} className="mr-1" />Serial {serialSupported ? '✓' : '✗'}
                        </Badge>
                        <Badge variant="outline" className={`text-[10px] ${btSupported ? 'border-electric-blue/30 text-electric-blue' : 'border-white/10 text-muted-foreground'}`}>
                            <Bluetooth size={10} className="mr-1" />Bluetooth {btSupported ? '✓' : '✗'}
                        </Badge>
                        {connectionMode && (
                            <Badge className={`text-[10px] ${isHardwareConnected ? 'bg-primary/20 text-primary border-primary/30' : 'bg-boltz-gold/20 text-boltz-gold border-boltz-gold/30'}`}>
                                {isHardwareConnected ? 'HARDWARE' : 'SIMULATED'}
                            </Badge>
                        )}
                    </div>
                </motion.div>

                {loading ? (
                    <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
                ) : (
                    <Tabs defaultValue="connect" className="w-full">
                        <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-auto mb-6">
                            <TabsTrigger value="connect" className="text-xs py-2"><Plugs size={14} className="mr-1" />Connect</TabsTrigger>
                            <TabsTrigger value="tune" className="text-xs py-2"><Cpu size={14} className="mr-1" />Tune</TabsTrigger>
                            <TabsTrigger value="flash" className="text-xs py-2"><FileArrowUp size={14} className="mr-1" />Flash</TabsTrigger>
                            <TabsTrigger value="profiles" className="text-xs py-2"><FloppyDisk size={14} className="mr-1" />Profiles</TabsTrigger>
                            <TabsTrigger value="shared" className="text-xs py-2"><ShareNetwork size={14} className="mr-1" />Community</TabsTrigger>
                            <TabsTrigger value="telemetry" className="text-xs py-2"><Gauge size={14} className="mr-1" />Live</TabsTrigger>
                        </TabsList>

                        <TabsContent value="connect">
                            <ConnectionPanel brands={brands} selectedBrand={selectedBrand} setSelectedBrand={setSelectedBrand}
                                selectedModel={selectedModel} setSelectedModel={setSelectedModel}
                                connectionMode={connectionMode} connectionInfo={connectionInfo} connecting={connecting}
                                isHardwareConnected={isHardwareConnected} readingParams={readingParams}
                                readAllParams={readAllParams} handleConnect={handleConnect} handleDisconnect={handleDisconnect}
                                serialSupported={serialSupported} btSupported={btSupported}
                                rawHex={rawHex} setRawHex={setRawHex} sendRawBytes={sendRawBytes}
                                byteLog={byteLog} faultCodes={faultCodes} />
                        </TabsContent>

                        <TabsContent value="tune">
                            <TuningControls paramGroups={paramGroups} params={params} setParams={setParams}
                                connectionMode={connectionMode} requestWrite={requestWrite} writingParam={writingParam}
                                profileName={profileName} setProfileName={setProfileName}
                                saveProfile={saveProfile} saving={saving} />
                        </TabsContent>

                        <TabsContent value="flash">
                            <FirmwareFlasher connectionMode={connectionMode} firmwareFile={firmwareFile}
                                setFirmwareFile={setFirmwareFile} flashProgress={flashProgress}
                                flashMessage={flashMessage} isFlashing={isFlashing}
                                handleFirmwareSelect={handleFirmwareSelect} flashFirmware={flashFirmware}
                                selectedBrand={selectedBrand} selectedModel={selectedModel} />
                        </TabsContent>

                        <TabsContent value="profiles">
                            <ProfileList profiles={profiles} loadProfile={loadProfile}
                                deleteProfile={deleteProfile} shareProfile={shareProfile} />
                        </TabsContent>

                        <TabsContent value="shared">
                            {sharedProfiles.length === 0 ? (
                                <Card className="glass-card text-center py-12">
                                    <ShareNetwork size={48} className="text-muted-foreground mx-auto mb-3" />
                                    <p className="text-muted-foreground">No shared profiles yet</p>
                                </Card>
                            ) : (
                                <div className="space-y-2">
                                    {sharedProfiles.map(p => (
                                        <Card key={p.profile_id} className="glass-card cursor-pointer hover:border-primary/20" onClick={() => loadProfile(p)}>
                                            <CardContent className="p-3 flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                                    <ShareNetwork size={18} className="text-primary" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold truncate">{p.name}</p>
                                                    <p className="text-[10px] text-muted-foreground">by {p.shared_by_name} • {p.controller_brand} {p.controller_model}</p>
                                                </div>
                                                <CaretRight size={16} className="text-muted-foreground" />
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="telemetry">
                            <TelemetryPanel connectionMode={connectionMode} isHardwareConnected={isHardwareConnected}
                                telemetry={telemetry} telemetryInterval={telemetryInterval}
                                startTelemetry={startTelemetry} />
                        </TabsContent>
                    </Tabs>
                )}
            </div>

            <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, paramId: null, value: null, label: '' })}>
                <AlertDialogContent className="bg-card border-plasma-red/30" data-testid="safety-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-plasma-red font-display flex items-center gap-2"><Warning size={20} /> Safety Warning</AlertDialogTitle>
                        <AlertDialogDescription className="text-muted-foreground">
                            You are about to write <strong className="text-foreground">{confirmDialog.label}</strong> = <strong className="text-foreground">{confirmDialog.value}</strong> to your controller.
                            <br /><br />This is a <span className="text-plasma-red font-bold">critical parameter</span> that directly affects motor/battery safety. Incorrect values can cause overcurrent, overheating, or permanent damage.
                            <br /><br />Are you sure you want to proceed?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="safety-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmWrite} className="bg-plasma-red text-white hover:bg-plasma-red/80" data-testid="safety-confirm-btn">I understand, write to controller</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
